import { HTMLAttributes } from "react"
import { Autocomplete, Box, CircularProgress, IconButton, TextField, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTrash } from "@fortawesome/pro-light-svg-icons"
import { faMinus, faPlus } from "@fortawesome/pro-solid-svg-icons"
import { multiply } from "dinero.js"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../../currency/useMoney"
import { toDinero } from "../../../../../../currency/currencyLib"
import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import {
    CUSTOM_PRODUCT_OPTION_ID,
    ProductionEditLine,
    ProductionProductOption,
    SetLineProductInput,
} from "../useProductionOrderEdit"
import { useProductionLineStock } from "../useProductionLineStock"
import { ProductionClampedName } from "./ProductionClampedName"

const useStyles = makeStyles()((theme) => ({
    row: {
        alignItems: "center",
    },
    rowView: {
        display: "flex",
        gap: ".75rem",
        padding: ".5rem .5rem",
    },
    rowEdit: {
        display: "grid",
        gridTemplateColumns: "9.875rem 1fr 4rem 7.5rem 2.5rem",
        columnGap: ".75rem",
        padding: ".5rem 0",
    },
    countView: {
        flexShrink: 0,
        width: "2.875rem",
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.2,
        color: theme.palette.tailwind.slate[800],
    },
    nameView: {
        fontWeight: 400,
        fontSize: "1rem",
        lineHeight: 1.2,
        color: theme.palette.tailwind.slate[800],
    },
    counter: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        flexShrink: 0,
    },
    counterButton: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        width: "2.5rem",
        height: "2.5rem",
        borderRadius: ".75rem",
        cursor: "pointer",
        background: theme.palette.tailwind.slate[800],
        color: theme.palette.common.white,
        fontSize: "1rem",
    },
    counterButtonDisabled: {
        cursor: "default",
        background: theme.palette.tailwind.slate[200],
        color: theme.palette.tailwind.slate[400],
    },
    countBox: {
        flexShrink: 0,
        width: "3.875rem",
        background: theme.palette.background.paper,
        "& .MuiOutlinedInput-root": {
            height: "2.5rem",
            borderRadius: ".25rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },
        "& .MuiOutlinedInput-input": {
            padding: ".5rem",
            textAlign: "center",
            fontSize: "1rem",
            color: theme.palette.tailwind.slate[800],
        },
    },
    productField: {
        position: "relative",
        flexGrow: 1,
        minWidth: "8rem",
        background: theme.palette.background.paper,
        "& .MuiOutlinedInput-root": {
            minHeight: "2.5rem",
            borderRadius: ".25rem",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.tailwind.slate[300],
        },
        "& .MuiAutocomplete-clearIndicator": {
            visibility: "visible",
        },
        "& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
            color: theme.palette.tailwind.slate[500],
        },
        "& .MuiFormHelperText-root": {
            position: "absolute",
            top: "100%",
            left: 0,
            margin: ".125rem 0 0",
        },
    },
    optionText: {
        display: "flex",
        flexDirection: "column",
    },
    optionName: {
        fontWeight: 500,
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[900],
    },
    optionPrice: {
        fontWeight: 400,
        fontSize: ".875rem",
        color: theme.palette.tailwind.slate[500],
    },
    optionStock: {
        fontWeight: 500,
        fontSize: ".75rem",
        color: theme.palette.tailwind.slate[500],
    },
    optionSoldOut: {
        color: theme.palette.tailwind.red[600],
    },
    unitPrice: {
        flexShrink: 0,
        width: "4rem",
        textAlign: "left",
        fontWeight: 400,
        fontSize: "1rem",
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
    },
    lineTotal: {
        flexShrink: 0,
        width: "7.5rem",
        textAlign: "left",
        fontWeight: 700,
        fontSize: "1rem",
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[800],
    },
    removeButton: {
        flexShrink: 0,
        width: "2.5rem",
        height: "2.5rem",
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        borderRadius: ".75rem",
        color: theme.palette.tailwind.slate[700],
        "& svg": {
            fontSize: "1rem",
        },
    },
}))

type Props = Readonly<{
    line: ProductionEditLine
    isEditing: boolean
    productOptions: ReadonlyArray<ProductionProductOption>
    isProductsLoading: boolean
    isEditLoading: boolean
    isTouched: boolean
    usedProductIds: ReadonlyArray<number>
    onCountChange: (key: string, value: number) => void
    onProductChange: (key: string, product: SetLineProductInput) => void
    onRemove: (key: string) => void
}>

export function ProductionOrderLineRow({
    line,
    isEditing,
    productOptions,
    isProductsLoading,
    isEditLoading,
    isTouched,
    usedProductIds,
    onCountChange,
    onProductChange,
    onRemove,
}: Props) {
    const { classes, cx } = useStyles()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})

    const { disableIncrease, handleCountInput, handleIncrease, isOptionSoldOut, getOptionStock, handleProductSelect } =
        useProductionLineStock({ line, productOptions, onCountChange, onProductChange })

    if (!isEditing) {
        return (
            <Box className={cx(classes.row, classes.rowView)}>
                <Typography variant="body1" component="span" className={classes.countView}>
                    {line.count}
                </Typography>
                <ProductionClampedName
                    name={line.name}
                    dialogTitleId="meeting-service-production-view-product-name-dialog-title"
                    titleClassName={classes.nameView}
                />
            </Box>
        )
    }

    const unitPriceLabel = moneyFactory.formatAsNumber(toDinero(line.itemPrice))
    const lineTotalLabel = moneyFactory.formatAsNumber(multiply(toDinero(line.itemPrice), line.count))

    // The line's current selection as an option. A product wish has a name + price but no productId, so it uses
    // a display-only sentinel id (never sent to the backend); a blank line (no name yet) shows the placeholder.
    const currentValue: ProductionProductOption | null = line.name
        ? { productId: line.productId ?? CUSTOM_PRODUCT_OPTION_ID, name: line.name, itemPrice: line.itemPrice }
        : null
    // Hide products already used by other lines
    const available = productOptions.filter(
        (option) => option.productId === line.productId || !usedProductIds.includes(option.productId),
    )
    const options =
        currentValue && !available.some((option) => option.productId === currentValue.productId)
            ? [currentValue, ...available]
            : available

    // Disabled while loading (until we know editability) and for extra-service lines (known bugs, Task 263).
    const isDisabled = isEditLoading || isProductsLoading || line.isExtraService

    // A blank line (no product picked) is invalid; show it only once touched, like FormikAutocomplete.
    const showProductRequired =
        isTouched && !line.isExtraService && line.productId === undefined && line.name.trim() === ""

    const renderProductOption = (optionProps: HTMLAttributes<HTMLLIElement>, option: ProductionProductOption) => {
        const { soldOut, label } = getOptionStock(option)
        return (
            <Box component="li" {...optionProps} key={option.productId}>
                <Box className={classes.optionText}>
                    <Typography component="span" className={classes.optionName}>
                        {option.name}
                    </Typography>
                    <Typography component="span" className={classes.optionPrice}>
                        {moneyFactory.formatAsNumber(toDinero(option.itemPrice))}
                    </Typography>
                    {label && (
                        <Typography
                            component="span"
                            className={cx(classes.optionStock, soldOut && classes.optionSoldOut)}
                        >
                            {label}
                        </Typography>
                    )}
                </Box>
            </Box>
        )
    }

    return (
        <Box className={cx(classes.row, classes.rowEdit)}>
            <Box className={classes.counter}>
                <Box
                    className={cx(classes.counterButton, isDisabled && classes.counterButtonDisabled)}
                    onClick={isDisabled ? undefined : () => onCountChange(line.key, Math.max(line.count - 1, 0))}
                >
                    <FontAwesomeIcon icon={faMinus} />
                </Box>
                <TextField
                    className={classes.countBox}
                    size="small"
                    value={line.count}
                    onChange={handleCountInput}
                    inputProps={{ maxLength: 4 }}
                    disabled={isDisabled}
                />
                <Box
                    // Greyed when the max is reached, but still clickable so the snackbar fires.
                    className={cx(
                        classes.counterButton,
                        (isDisabled || disableIncrease) && classes.counterButtonDisabled,
                    )}
                    onClick={isDisabled ? undefined : handleIncrease}
                >
                    <FontAwesomeIcon icon={faPlus} />
                </Box>
            </Box>

            <Autocomplete<ProductionProductOption>
                className={classes.productField}
                options={options}
                value={currentValue}
                disabled={isDisabled}
                loading={isProductsLoading}
                onChange={(_event, value) => handleProductSelect(value)}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.productId === value.productId}
                getOptionDisabled={isOptionSoldOut}
                renderOption={renderProductOption}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="small"
                        placeholder={l10n.getString("meeting-service-production-view-product-search-placeholder")}
                        error={showProductRequired}
                        helperText={
                            showProductRequired
                                ? l10n.getString("meeting-service-production-view-product-required")
                                : undefined
                        }
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {isProductsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            <Typography variant="body1" className={classes.unitPrice}>
                {unitPriceLabel}
            </Typography>

            <Typography variant="body1" className={classes.lineTotal}>
                {lineTotalLabel}
            </Typography>

            <LocalizedStrict id="meeting-service-production-view-remove-line-button" attrs={{ "aria-label": true }}>
                <IconButton
                    size="small"
                    className={classes.removeButton}
                    aria-label="Remove"
                    disabled={isDisabled}
                    onClick={() => onRemove(line.key)}
                >
                    <FontAwesomeIcon icon={faTrash} />
                </IconButton>
            </LocalizedStrict>
        </Box>
    )
}
